/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Button } from '@mui/material';
import { InputLabel, Select, RadioGroup, PaymentForm, HtmlRender } from 'components/elements';
import { feeTypes } from 'const';
import { useFetchGrossFamilyIncome, useFetchFeeTypes, useFetchConfiguration, useFetchInsurer } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function FeesForm({ onSubmit, formId, submitRef, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { data: grossFamilyIncomes } = useFetchGrossFamilyIncome();
  const { data: feesOptions } = useFetchFeeTypes();
  const { data: insurerOptions } = useFetchInsurer();

  const { data, isEnabledField, isRequiredField } = useFetchConfiguration();

  const { handleSubmit, control, watch, getValues } = useForm({
    mode: 'onSubmit',
    defaultValues: restProps,
  });
  const feeType = watch('feeType');

  const onSubmitForm = (goBack) => {
    const values = getValues();
    onSubmit(values, goBack);
  };

  return (
    <div className={classes.feesFormWrapper}>
      {!restProps.isServiceRequest && <h1 className={classes.formHeader}>{t('fees')}</h1>}
      <form id={formId} className={classes.feesForm} onSubmit={handleSubmit(() => onSubmitForm(false))}>
        {isEnabledField('summary') && (
          <Typography variant="body1" component="h6">
            <HtmlRender data={data.summary.translation} htmlProfile />
          </Typography>
        )}
        {isEnabledField('feeType') && (
          <div className={classes.feeTypes}>
            <Controller
              name="feeType"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('feeType'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <RadioGroup
                  options={feesOptions}
                  helperText={error ? error.message : null}
                  error={!!error}
                  label={<InputLabel text={t('fee_type')} caption required={isRequiredField('feeType')} />}
                  column
                  {...restField}
                />
              )}
            />
            {feeType === feeTypes.slidingScale && (
              <div className={classes.incomeSelectField}>
                <Controller
                  name="grossFamilyIncome"
                  control={control}
                  rules={{
                    required: {
                      value: isRequiredField('feeType'),
                      message: t('please_fill_in_this_field'),
                    },
                  }}
                  render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                    <Select
                      options={grossFamilyIncomes}
                      defaultValue=""
                      label={<InputLabel text={t('gross_family_income')} required={isRequiredField('feeType')} />}
                      helperText={error ? error.message : t('this_will_be_used_to_calculate_fees_based_on_sliding_scale')}
                      error={!!error}
                      {...restField}
                    />
                  )}
                />
              </div>
            )}
          </div>
        )}
        {!restProps.isServiceRequest && isEnabledField('payments') && (
          <div className={classes.payment}>
            <Controller
              name="paymentInformation"
              control={control}
              rules={{
                validate: (value) => {
                  if (value?.complete) {
                    return null;
                  }
                  if (isRequiredField('payments')) {
                    return t('please_fill_in_this_field');
                  }
                  return null;
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <PaymentForm
                  error={!!error}
                  helperText={error ? error.message : null}
                  {...restField}
                  label={<InputLabel text={t('payment_information')} required={isRequiredField('payments')} />}
                  required={isRequiredField('payments')}
                />
              )}
            />
          </div>
        )}

        {isEnabledField('isPatientInsured') && (
          <div className={classes.insurer}>
            <Controller
              name="isPatientInsured"
              control={control}
              rules={{
                required: {
                  value: isRequiredField('isPatientInsured'),
                  message: t('please_fill_in_this_field'),
                },
              }}
              render={({ field: { ref, ...restField }, fieldState: { error } }) => (
                <RadioGroup
                  options={insurerOptions}
                  error={!!error}
                  helperText={error ? error.message : null}
                  {...restField}
                  label={
                    <>
                      <InputLabel text={t('are_you_covered_by_insurance')} required={isRequiredField('isPatientInsured')} />
                      <Typography className={classes.insurerLabel} variant="body2" component="p">
                        {t('please_verify_the_details_of_your_plan')}
                      </Typography>
                    </>
                  }
                  required={isRequiredField('isPatientInsured')}
                />
              )}
            />
          </div>
        )}
        {restProps.isServiceRequest && (
          <div className={classes.controlButton}>
            <Button onClick={() => onSubmitForm(true)} ref={submitRef} variant="outlined" color="primary" size="large" className={classes.backButton}>
              {t('back')}
            </Button>
            <Button type="submit" variant="contained" color="primary" size="large" className={classes.serviceBackButton}>
              {t('next')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

FeesForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submitRef: PropTypes.objectOf(PropTypes.any).isRequired,
  formId: PropTypes.string.isRequired,
  feeType: PropTypes.string,
  grossFamilyIncome: PropTypes.string,
  isPatientInsured: PropTypes.string,
};

FeesForm.defaultProps = {
  feeType: '',
  grossFamilyIncome: '',
  isPatientInsured: '',
};

export default FeesForm;
